import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  FileCheck2,
  GitBranch,
  Search,
  ShieldCheck,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PulseBadge } from "@/components/site/PulseBadge";
import { useLocale } from "@/lib/i18n";
import shopifyDev1x from "@/assets/shopify-dev-badge-1x.webp";
import shopifyDev2x from "@/assets/shopify-dev-badge-2x.webp";
import shopifySelect1x from "@/assets/shopify-select-badge-1x.webp";
import shopifySelect2x from "@/assets/shopify-select-badge-2x.webp";
import forbes1x from "@/assets/forbes-logo-1x.webp";
import forbes2x from "@/assets/forbes-logo-2x.webp";
import apLogo from "@/assets/ap-logo.svg";
import europapress1x from "@/assets/europapress-logo-1x.webp";
import europapress2x from "@/assets/europapress-logo-2x.webp";
import lavanguardia from "@/assets/lavanguardia-logo.svg";
import techbehemoths1x from "@/assets/techbehemoths-logo-1x.webp";
import techbehemoths2x from "@/assets/techbehemoths-logo-2x.webp";
import { quotePath, quoteSearch } from "@/lib/quote-links";
import { LazyHydrate } from "@/components/site/LazyHydrate";
import { ClientsSection } from "@/components/home/ClientsSection";
import { WorkSection } from "@/components/home/WorkSection";
import { TestimonialsSection } from "@/components/home/TestimonialsSection";
import type { HomeProject } from "@/components/home/types";
import { HOME_FAQS } from "@/components/home/home-faqs";
import { REVIEWS_META } from "@/lib/reviews";

const METHOD_ICONS = [Search, FileCheck2, GitBranch, ShieldCheck];

export function CorporateHomeContent({
  projects,
  homeLimit = 6,
}: {
  projects: HomeProject[];
  homeLimit?: number;
}) {
  const locale = useLocale();
  const t = locale === "es" ? COPY.es : COPY.en;
  const featured = projects.find((project) => project.featured) ?? projects[0];
  const othersLimit = Math.max(0, homeLimit - 1);
  const others = projects.filter((project) => project.id !== featured?.id).slice(0, othersLimit);
  const quoteSource = locale === "es" ? "/es" : "/";
  const proofItems = [
    { value: "2015", label: t.proof.partner, href: REVIEWS_META.partnerUrl, external: true },
    { value: "750+", label: t.proof.engagements, href: locale === "es" ? "/es/proyectos" : "/work" },
    { value: "35", label: t.proof.markets, href: locale === "es" ? "/es/nosotros" : "/about" },
    { value: "30+", label: t.proof.migrations, href: locale === "es" ? "/es/proyectos" : "/work" },
    { value: String(REVIEWS_META.count), label: t.proof.reviews, href: REVIEWS_META.partnerUrl, external: true },
  ];

  return (
    <>
      <section className="site-home-hero relative overflow-hidden border-b border-border" data-surface="dark">
        <div className="site-home-hero-grid absolute inset-0" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-px bg-[#fafaf8]/20" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-6 py-16 lg:grid-cols-12 lg:items-center lg:py-24">
          <div className="lg:col-span-7">
            <p className="text-xs font-medium uppercase tracking-[0.26em] text-[#ccff00]">{t.hero.eyebrow}</p>
            <h1 className="mt-7 max-w-5xl font-display text-5xl font-semibold leading-[0.98] tracking-tight text-foreground text-balance sm:text-6xl lg:text-7xl">
              {t.hero.titleLead}{" "}
              <span className="text-[#a5b4ba]">{t.hero.titleAccent}</span>
            </h1>
            <p className="mt-7 max-w-3xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {t.hero.body}
            </p>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground">{t.hero.qualifier}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to={quotePath(locale)}
                search={quoteSearch({ source: quoteSource })}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] px-7 py-3.5 text-sm font-semibold text-[#0a0a0a] transition-colors hover:bg-[#d8ff3d]"
              >
                {t.hero.primary}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
              <Link
                to={locale === "es" ? "/es/proyectos" : "/work"}
                className="inline-flex items-center justify-center rounded-full border border-[#fafaf8]/28 bg-transparent px-7 py-3.5 text-sm font-medium text-[#fafaf8] transition-colors hover:border-[#fafaf8]/55 hover:bg-[#fafaf8]/10"
              >
                {t.hero.secondary}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="hero-technical-panel relative overflow-hidden rounded-2xl border border-border p-6 shadow-2xl shadow-black/30 backdrop-blur-sm sm:p-8">
              <div className="absolute inset-x-0 top-0 h-1 bg-[#ccff00]" aria-hidden="true" />
              <div className="flex items-start justify-between gap-6 border-b border-border pb-5">
                <div>
                  <p className="technical-panel-eyebrow text-[10px] font-semibold uppercase tracking-[0.24em]">{t.method.eyebrow}</p>
                  <h2 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight text-foreground">{t.method.title}</h2>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">SGT / 01</span>
              </div>
              <ol className="divide-y divide-border">
                {t.method.steps.map((step, index) => {
                  const Icon = METHOD_ICONS[index] ?? CheckCircle2;
                  return (
                    <li key={step.title} className="grid grid-cols-[2.25rem_1fr] gap-4 py-5">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-[#fafaf8]/18 bg-[#fafaf8]/8 text-[#a5b4ba]">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div>
                        <div className="flex items-baseline justify-between gap-4">
                          <h3 className="font-display text-base font-semibold text-foreground">{step.title}</h3>
                          <span className="technical-panel-row-index font-mono text-[10px]">0{index + 1}</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <p className="border-t border-border pt-5 text-xs leading-relaxed text-muted-foreground">{t.method.note}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="site-home-proof border-b border-border" data-surface="methodology">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-7 px-6 py-8 sm:grid-cols-3 lg:grid-cols-5">
          {proofItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noreferrer noopener" : undefined}
              className="group border-l border-border pl-4"
            >
              <span className="font-display text-2xl font-semibold tracking-tight text-foreground transition-colors group-hover:text-[#0a0a0a]">{item.value}</span>
              <span className="mt-1 block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{item.label}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="site-home-situations border-b border-border py-24 sm:py-28" data-surface="methodology">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.situations.eyebrow}</p>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl">{t.situations.title}</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">{t.situations.body}</p>
          </Reveal>
          <div className="grid border-t border-border sm:grid-cols-2 lg:col-span-8">
            {t.situations.items.map((item, index) => (
              <Reveal
                key={item.href}
                delay={(index % 4) * 45}
                className="border-b border-border sm:odd:pr-8 sm:even:border-l sm:even:border-border sm:even:pl-8"
              >
                <a href={item.href} className="group flex min-h-24 items-start justify-between gap-5 py-6">
                  <span className="max-w-sm text-sm leading-relaxed text-foreground/85 transition-colors group-hover:text-[#0a0a0a]">{item.label}</span>
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-[#4f5452]/50 transition-transform group-hover:translate-x-1 dark:text-[#a5b4ba]/50" aria-hidden="true" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <LazyHydrate as="section" className="site-home-work py-24 sm:py-28" data-surface="dark">
        <WorkSection locale={locale} work={t.work} featured={featured} others={others} />
      </LazyHydrate>

      <section className="site-home-capabilities mx-auto max-w-7xl px-6 py-24 sm:py-28" data-surface="light">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.capabilities.eyebrow}</p>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl">{t.capabilities.title}</h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">{t.capabilities.body}</p>
              <Link to={locale === "es" ? "/es/servicios" : "/services"} className="group mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#4f5452]">
                {t.capabilities.all}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </div>
            <div className="grid border-y border-border sm:grid-cols-2 lg:col-span-7">
              {t.capabilities.items.map((item, index) => (
                <Reveal
                  key={item.title}
                  delay={index * 60}
                  className="border-b border-border py-7 sm:odd:pr-8 sm:even:border-l sm:even:border-border sm:even:pl-8 sm:[&:nth-last-child(-n+2)]:border-b-0"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6c706f]">0{index + 1}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-foreground">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                    {item.links.map((link) => (
                      <a key={link.href} href={link.href} className="text-sm font-medium text-foreground underline decoration-[#0a0a0a]/25 underline-offset-4 transition-colors hover:text-[#0a0a0a] hover:decoration-[#0a0a0a]">
                        {link.label}
                      </a>
                    ))}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="site-operating-model py-24 sm:py-28" data-surface="dark">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#a5b4ba]">{t.process.eyebrow}</p>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.04] tracking-tight sm:text-5xl">{t.process.title}</h2>
              <p className="mt-5 text-base leading-relaxed text-white/65">{t.process.body}</p>
              <p className="mt-5 text-sm font-medium leading-relaxed text-[#ccff00]">{t.process.continuity}</p>
              <div className="mt-7"><PulseBadge locale={locale} variant="inline" /></div>
            </Reveal>
            <ol className="border-t border-white/15 lg:col-span-8">
              {t.process.steps.map((step, index) => (
                <Reveal as="li" key={step.title} delay={index * 60} className="grid gap-4 border-b border-white/15 py-7 sm:grid-cols-[4rem_1fr]">
                  <span className="font-mono text-xs text-[#ccff00]">0{index + 1}</span>
                  <div className="grid gap-3 sm:grid-cols-2 sm:gap-8">
                    <h3 className="font-display text-xl font-semibold tracking-tight">{step.title}</h3>
                    <p className="text-sm leading-relaxed text-white/65">{step.body}</p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <LazyHydrate as="section" className="border-b border-border py-12">
        <ClientsSection header={t.clientsHeader} />
      </LazyHydrate>

      <LazyHydrate as="section" className="mx-auto max-w-7xl px-6 py-24 sm:py-28">
        <TestimonialsSection reviews={t.reviews} />
      </LazyHydrate>

      <section className="site-home-press border-y border-border" data-surface="light">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-3">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.press.eyebrow}</p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-foreground/75">{t.press.body}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-6 opacity-80 lg:col-span-9 lg:flex-nowrap lg:justify-end">
            <img src={forbes1x} srcSet={`${forbes1x} 1x, ${forbes2x} 2x`} alt="Forbes recognition logo" width={120} height={28} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            <img src={apLogo} alt="Associated Press logo" width={32} height={38} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            <img src={lavanguardia} alt="La Vanguardia press logo" width={140} height={28} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            <img src={europapress1x} srcSet={`${europapress1x} 1x, ${europapress2x} 2x`} alt="Europa Press logo" width={160} height={48} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            <img src={techbehemoths1x} srcSet={`${techbehemoths1x} 1x, ${techbehemoths2x} 2x`} alt="TechBehemoths recognition" width={120} height={40} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            <a
              href={REVIEWS_META.partnerUrl}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={t.press.partnerLink}
              className="site-home-press-partner group inline-flex items-center gap-5 border-l border-border pl-6"
            >
              <img src={shopifyDev1x} srcSet={`${shopifyDev1x} 1x, ${shopifyDev2x} 2x`} alt="Shopify Developer Plus partner badge" width={120} height={48} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
              <img src={shopifySelect1x} srcSet={`${shopifySelect1x} 1x, ${shopifySelect2x} 2x`} alt="Shopify Select Partner badge" width={120} height={40} loading="lazy" decoding="async" className="h-8 w-auto shrink-0" />
            </a>
          </div>
        </div>
      </section>

      <section className="site-home-engagements border-b border-border py-24 sm:py-28" data-surface="light">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:items-start">
          <Reveal className="lg:col-span-7">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.engagements.eyebrow}</p>
            <h2 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl">{t.engagements.title}</h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">{t.engagements.body}</p>
          </Reveal>
          <div className="border-y border-border lg:col-span-5">
            {t.engagements.paths.map((path) => (
              <a key={path.href} href={path.href} className="group grid grid-cols-[1fr_auto] gap-6 border-b border-border py-6 last:border-b-0">
                <div>
                  <h3 className="font-display text-xl font-semibold text-foreground transition-colors group-hover:text-[#0a0a0a]">{path.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{path.body}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-[#4f5452]/50 transition-transform group-hover:translate-x-1 dark:text-[#a5b4ba]/50" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="site-home-faq border-t border-border py-24 sm:py-28" data-surface="light">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.faq.eyebrow}</p>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground">{t.faq.title}</h2>
          </Reveal>
          <div className="divide-y divide-border border-y border-border lg:col-span-8">
            {(locale === "es" ? HOME_FAQS.es : HOME_FAQS.en).map((faq) => (
              <details key={faq.q} className="group py-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                  <h3 className="font-display text-lg font-semibold tracking-tight text-foreground">{faq.q}</h3>
                  <span className="text-[#4f5452] transition-transform group-open:rotate-45 dark:text-[#a5b4ba]" aria-hidden="true">+</span>
                </summary>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:py-32">
        <Reveal>
          <div className="relative grid gap-10 border-y border-border py-12 lg:grid-cols-12 lg:items-end">
            <div className="absolute inset-x-0 top-0 h-px bg-[#ccff00]" aria-hidden="true" />
            <div className="lg:col-span-8">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#4f5452]">{t.cta.eyebrow}</p>
              <h2 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl lg:text-6xl">{t.cta.title}</h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">{t.cta.body}</p>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-4 lg:items-end">
              <Link
                to={quotePath(locale)}
                search={quoteSearch({ source: quoteSource })}
                className="group inline-flex items-center gap-2 rounded-full bg-[#ccff00] px-7 py-3.5 text-sm font-semibold text-[#0a0a0a] transition-colors hover:bg-[#d8ff3d]"
              >
                {t.cta.primary}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
              <Link to={locale === "es" ? "/es/contacto" : "/contact"} className="text-sm text-muted-foreground hover:text-[#4f5452] dark:hover:text-[#a5b4ba]">
                {t.cta.secondary} →
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}

const COPY = {
  en: {
    hero: {
      eyebrow: "Shopify Select Partner · Engineering since 2015",
      titleLead: "Build a Shopify operation that can support",
      titleAccent: "what comes next.",
      body: "We help established DTC and B2B brands remove the technical and operational constraints affecting conversion, organic visibility, release speed and reliability—from storefront performance to migrations, integrations and Shopify Plus.",
      qualifier: "Bring us the business constraint. We define the responsible scope, implement it and own the path to production.",
      primary: "Get a scoped recommendation",
      secondary: "See selected work",
    },
    method: {
      eyebrow: "How we reduce implementation risk",
      title: "A controlled path from problem to production",
      note: "Scope, assumptions, acceptance criteria, responsibilities and exclusions are agreed before implementation begins.",
      steps: [
        { title: "Evidence", body: "Identify the affected journey, system and business impact." },
        { title: "Scope", body: "Define the smallest responsible intervention and acceptance criteria." },
        { title: "Implementation", body: "Build, stage and validate the change against the agreed scope." },
        { title: "Release", body: "Publish with approval, measurement, ownership and a rollback path." },
      ],
    },
    proof: {
      partner: "Shopify Partner since",
      engagements: "Shopify projects and engagements",
      markets: "Markets served",
      migrations: "Complex migrations",
      reviews: "Verified merchant reviews",
    },
    situations: {
      eyebrow: "Recognizable situations",
      title: "When Shopify starts creating friction across the business.",
      body: "Start with the situation your team can recognize. We determine the technical intervention and the dependencies that must remain protected.",
      items: [
        { label: "Performance problems are affecting conversion or paid acquisition efficiency.", href: "/shopify-performance" },
        { label: "Product, cart or checkout experiences receive traffic but underperform.", href: "/shopify-cro" },
        { label: "Organic visibility has stalled, declined or become difficult to diagnose.", href: "/shopify-technical-seo" },
        { label: "Apps and customizations are conflicting or duplicating responsibility.", href: "/shopify-app-development" },
        { label: "A migration or rebuild creates operational, SEO or revenue risk.", href: "/shopify-migration" },
        { label: "Shopify is disconnected from ERP, PIM, WMS, 3PL or analytics systems.", href: "/shopify-integration-services" },
        { label: "Releases have become slow, risky or dependent on multiple vendors.", href: "/retainer" },
        { label: "The theme or Shopify Plus architecture no longer supports the operating model.", href: "/shopify-plus-agency" },
      ],
    },
    work: {
      eyebrow: "Selected work",
      title: "Business context, technical decisions and measurable evidence belong together.",
      all: "Review all projects",
    },
    capabilities: {
      eyebrow: "One commerce operation",
      title: "Four areas where constraints emerge.",
      body: "Most ecommerce problems cross disciplines. We identify the primary constraint while preserving the customer journey, platform and operational systems around it.",
      all: "Find the right intervention",
      items: [
        {
          title: "Experience",
          body: "Performance and conversion when qualified traffic meets a slow, unclear or unreliable storefront.",
          links: [
            { label: "Performance", href: "/shopify-performance" },
            { label: "CRO", href: "/shopify-cro" },
          ],
        },
        {
          title: "Visibility",
          body: "Technical SEO, GEO and measurement when crawling, indexation or evidence limits discovery.",
          links: [
            { label: "Technical SEO", href: "/shopify-technical-seo" },
            { label: "GEO Readiness", href: "/geo-readiness" },
          ],
        },
        {
          title: "Platform",
          body: "Development, migrations, applications and Shopify Plus architecture when the current surface cannot carry what comes next.",
          links: [
            { label: "Development", href: "/shopify-experts" },
            { label: "Migrations", href: "/shopify-migration" },
            { label: "Shopify Plus", href: "/shopify-plus-agency" },
          ],
        },
        {
          title: "Operations",
          body: "Integrations, analytics and continuous engineering when Shopify must coordinate reliably with the wider business.",
          links: [
            { label: "Integrations", href: "/shopify-integration-services" },
            { label: "Analytics", href: "/analytics-ai" },
            { label: "Continuous engineering", href: "/retainer" },
          ],
        },
      ],
    },
    process: {
      eyebrow: "Delivery discipline",
      title: "Senior context stays connected to the release.",
      body: "PULSE supports the workflow, while technical accountability remains human and visible from the first decision through production.",
      continuity: "The people who help define the intervention remain connected to implementation, QA and release.",
      steps: [
        { title: "Frame the business constraint", body: "Document the customer journey, operational dependency and technical surface together." },
        { title: "Approve the scope", body: "Make deliverables, assumptions, exclusions, timing and acceptance criteria reviewable before kickoff." },
        { title: "Build and validate", body: "Move changes through staging, representative QA and stakeholder review." },
        { title: "Release and observe", body: "Make production authority, rollback, measurement and next ownership explicit." },
      ],
    },
    clientsHeader: "Selected DTC and B2B teams we have supported",
    reviews: {
      eyebrow: "Shopify Partner Directory",
      title: "Verified experience from the teams closest to the work.",
      caption: "{count} verified reviews",
      showAll: "Read all {count} reviews",
      showLess: "Show fewer reviews",
    },
    press: {
      eyebrow: "Recognition and coverage",
      body: "Independent coverage and industry recognition support the public record behind the company.",
      partnerLink: "View our Shopify Partner Directory profile",
    },
    engagements: {
      eyebrow: "Ways to engage",
      title: "Start with the smallest engagement that can responsibly own the constraint.",
      body: "Choose a defined project, continuous engineering or Shopify Plus ownership according to dependency and cadence—not a generic package ladder.",
      paths: [
        { title: "Defined project", body: "A bounded diagnosis, implementation, migration or build with written acceptance.", href: "/pricing" },
        { title: "Continuous engineering", body: "Prioritized senior capacity for a real roadmap and recurring releases.", href: "/retainer" },
        { title: "Shopify Plus ownership", body: "Architecture, B2B, Markets, Functions, checkout and complex operations.", href: "/shopify-plus-agency" },
      ],
    },
    faq: { eyebrow: "Frequently asked", title: "Questions before the first scope." },
    cta: {
      eyebrow: "Start with the business constraint",
      title: "Tell us what Shopify is preventing your team from doing.",
      body: "Share the current setup, affected customer journey and business decision. We will recommend the smallest responsible scope, timeline and engagement model.",
      primary: "Get a scoped recommendation",
      secondary: "Contact the team",
    },
  },
  es: {
    hero: {
      eyebrow: "Shopify Select Partner · Ingeniería desde 2015",
      titleLead: "Agencia Shopify senior para",
      titleAccent: "la siguiente etapa de tu negocio.",
      body: "Ayudamos a marcas DTC y B2B establecidas a eliminar las restricciones técnicas y operativas que afectan conversión, visibilidad orgánica, velocidad de entrega y confiabilidad: desde el rendimiento de la tienda hasta migraciones, integraciones y Shopify Plus.",
      qualifier: "Cuéntanos qué está limitando al negocio. Definimos el alcance responsable, lo implementamos y asumimos la entrega hasta producción.",
      primary: "Solicitar recomendación de alcance",
      secondary: "Ver proyectos seleccionados",
    },
    method: {
      eyebrow: "Cómo reducimos el riesgo de implementación",
      title: "Un camino controlado del problema a producción",
      note: "Acordamos alcance, supuestos, criterios de aceptación, responsables y exclusiones antes de comenzar la implementación.",
      steps: [
        { title: "Evidencia", body: "Identificar el recorrido afectado, el sistema y el impacto para el negocio." },
        { title: "Alcance", body: "Definir la intervención responsable y los criterios de aceptación." },
        { title: "Implementación", body: "Construir, preparar y validar el cambio contra el alcance aprobado." },
        { title: "Publicación", body: "Publicar con aprobación, medición, responsables y plan de reversión." },
      ],
    },
    proof: {
      partner: "Shopify Partner desde",
      engagements: "Proyectos e intervenciones Shopify",
      markets: "Mercados atendidos",
      migrations: "Migraciones complejas",
      reviews: "Reseñas verificadas",
    },
    situations: {
      eyebrow: "Situaciones reconocibles",
      title: "Cuando Shopify empieza a frenar al negocio.",
      body: "Empieza por la situación que reconoce tu equipo. Nosotros determinamos la intervención técnica y las dependencias que deben protegerse.",
      items: [
        { label: "El rendimiento está afectando conversión o eficiencia de adquisición.", href: "/es/rendimiento" },
        { label: "Las páginas de producto, carrito o checkout reciben tráfico, pero no rinden.", href: "/es/cro-shopify" },
        { label: "La visibilidad orgánica se estancó, cayó o es difícil de diagnosticar.", href: "/es/seo-tecnico-shopify" },
        { label: "Las aplicaciones y personalizaciones generan conflictos o responsabilidades duplicadas.", href: "/es/desarrollo-apps-shopify" },
        { label: "Una migración o reconstrucción crea riesgo operativo, de SEO o ingresos.", href: "/es/migracion-shopify" },
        { label: "Shopify está desconectado de ERP, PIM, WMS, 3PL o analítica.", href: "/es/integraciones-shopify" },
        { label: "Publicar cambios se volvió lento, riesgoso o dependiente de varios proveedores.", href: "/es/retainer" },
        { label: "El tema o la arquitectura Shopify Plus ya no sostienen la operación.", href: "/es/agencia-shopify-plus" },
      ],
    },
    work: {
      eyebrow: "Proyectos seleccionados",
      title: "El contexto del negocio, las decisiones técnicas y la evidencia deben verse juntos.",
      all: "Ver todos los proyectos",
    },
    capabilities: {
      eyebrow: "Una operación de comercio",
      title: "Cuatro áreas donde aparecen las restricciones.",
      body: "La mayoría de los problemas de ecommerce cruza varias disciplinas. Identificamos la restricción principal sin perder el recorrido del cliente, la plataforma ni los sistemas operativos que la rodean.",
      all: "Encontrar la intervención adecuada",
      items: [
        {
          title: "Experiencia",
          body: "Rendimiento y conversión cuando el tráfico cualificado llega a una tienda lenta, poco clara o poco confiable.",
          links: [
            { label: "Rendimiento", href: "/es/rendimiento" },
            { label: "CRO", href: "/es/cro-shopify" },
          ],
        },
        {
          title: "Visibilidad",
          body: "SEO técnico, GEO y medición cuando el rastreo, la indexación o la evidencia limitan el descubrimiento.",
          links: [
            { label: "SEO técnico", href: "/es/seo-tecnico-shopify" },
            { label: "Auditoría GEO", href: "/es/auditoria-geo" },
          ],
        },
        {
          title: "Plataforma",
          body: "Desarrollo, migraciones, aplicaciones y arquitectura Shopify Plus cuando la solución actual ya no sostiene lo que sigue.",
          links: [
            { label: "Desarrollo", href: "/es/desarrollo-shopify" },
            { label: "Migraciones", href: "/es/migracion-shopify" },
            { label: "Shopify Plus", href: "/es/agencia-shopify-plus" },
          ],
        },
        {
          title: "Operación",
          body: "Integraciones, analítica e ingeniería continua cuando Shopify debe coordinarse de forma confiable con el resto del negocio.",
          links: [
            { label: "Integraciones", href: "/es/integraciones-shopify" },
            { label: "Analítica", href: "/es/analitica-ai" },
            { label: "Ingeniería continua", href: "/es/retainer" },
          ],
        },
      ],
    },
    process: {
      eyebrow: "Disciplina de entrega",
      title: "El contexto senior permanece conectado hasta la publicación.",
      body: "PULSE apoya el proceso, mientras la responsabilidad técnica sigue siendo humana y visible desde la primera decisión hasta producción.",
      continuity: "Quienes ayudan a definir la intervención permanecen conectados con la implementación, las pruebas y la publicación.",
      steps: [
        { title: "Enmarcar la restricción", body: "Documentar juntos el recorrido del cliente, la dependencia operativa y la superficie técnica." },
        { title: "Aprobar el alcance", body: "Hacer revisables los entregables, supuestos, exclusiones, tiempos y criterios de aceptación antes de comenzar." },
        { title: "Construir y validar", body: "Pasar los cambios por un entorno de pruebas, QA representativo y revisión de los equipos involucrados." },
        { title: "Publicar y observar", body: "Definir autoridad de producción, reversión, medición y responsabilidad posterior." },
      ],
    },
    clientsHeader: "Equipos DTC y B2B que hemos apoyado",
    reviews: {
      eyebrow: "Directorio de Shopify Partners",
      title: "Experiencia verificada por los equipos más cercanos al trabajo.",
      caption: "{count} reseñas verificadas",
      showAll: "Leer las {count} reseñas",
      showLess: "Mostrar menos reseñas",
    },
    press: {
      eyebrow: "Reconocimiento y cobertura",
      body: "La cobertura independiente y el reconocimiento de la industria respaldan el registro público de la empresa.",
      partnerLink: "Ver nuestro perfil en el directorio de Shopify Partners",
    },
    engagements: {
      eyebrow: "Formas de trabajar",
      title: "Empieza con la modalidad más pequeña que pueda asumir la restricción de forma responsable.",
      body: "Elige un proyecto definido, ingeniería continua o responsabilidad Shopify Plus según las dependencias y la cadencia, no según una lista genérica de paquetes.",
      paths: [
        { title: "Proyecto definido", body: "Diagnóstico, implementación, migración o desarrollo acotado con aceptación escrita.", href: "/es/precios" },
        { title: "Ingeniería continua", body: "Capacidad senior priorizada para un plan real y publicaciones recurrentes.", href: "/es/retainer" },
        { title: "Responsabilidad Shopify Plus", body: "Arquitectura, B2B, Markets, Functions, checkout y operaciones complejas.", href: "/es/agencia-shopify-plus" },
      ],
    },
    faq: { eyebrow: "Preguntas frecuentes", title: "Preguntas antes del primer alcance." },
    cta: {
      eyebrow: "Empieza con la restricción del negocio",
      title: "Cuéntanos qué está impidiendo Shopify que tu equipo pueda hacer.",
      body: "Comparte la configuración actual, el recorrido afectado y la decisión del negocio. Recomendaremos el alcance, tiempo y modalidad de trabajo responsables más pequeños.",
      primary: "Solicitar recomendación de alcance",
      secondary: "Contactar al equipo",
    },
  },
} as const;
